import { Flex, Text } from "@chakra-ui/layout";
import { Grid } from "@chakra-ui/react";
import { FormikContextType, useField, useFormikContext } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { addressLookup } from "src/components/Boundary/services";
import { AddressAutocomplete } from "src/components/Inputs/Address/Autocomplete/AddressAutocompleteInput";
import { InputWithValidationControl } from "src/components/Inputs/InputWithValidationControl";
import { PersonAddress, PersonAddressForm } from "src/schemas/Address";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";

export type Props = {
  isRequired?: boolean;
  isDisabled?: boolean;
  fieldPrefix?: string;
  autoFocus?: boolean;
};
type Geom = {
  type: "Point";
  coordinates?: [number, number] | null;
};

function decimalToDMS(decimal: number, isLat: boolean): string {
  const abs = Math.abs(decimal);
  const degrees = Math.floor(abs);
  const minutesDecimal = (abs - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = (minutesDecimal - minutes) * 60;
  const dir = isLat ? (decimal >= 0 ? "N" : "S") : decimal >= 0 ? "E" : "W";
  return `${degrees}°${minutes}'${seconds.toFixed(1)}"${dir}`;
}

function googleMapsPlaceUrl(lat: number, lng: number): string {
  const placeName = `${decimalToDMS(lat, true)} ${decimalToDMS(lng, false)}`;
  const encoded = encodeURIComponent(placeName).replace(/%20/g, "+");
  return `https://www.google.com/maps/place/${encoded}/@${lat},${lng}`;
}

const Coordinates: React.FC<{
  withPrefix: (name: string) => string;
  formik: FormikContextType<PersonAddressForm>;
  isValid: boolean;
  geom: Geom | null;
  showLatLngForm: boolean;
  hideCoordinateWarning: boolean;
  onClickAcceptEnterCoordinates: () => void;
  onRetryGeocode: () => Promise<void>;
  isRetryingGeocode: boolean;
  hasFailedGeocode: boolean;
}> = ({
  withPrefix,
  formik,
  isValid,
  geom,
  showLatLngForm,
  hideCoordinateWarning,
  onClickAcceptEnterCoordinates,
  onRetryGeocode,
  isRetryingGeocode,
  hasFailedGeocode,
}) => {
  const [latValue, setLatValue] = useState<string>("");
  const [lngValue, setLngValue] = useState<string>("");

  useEffect(() => {
    if (!showLatLngForm) {
      setLatValue("");
      setLngValue("");
    }
  }, [showLatLngForm]);

  return (
    <Flex direction={"column"} width="100%">
      {geom?.coordinates && !showLatLngForm && (
        <Text color={"gray.600"} fontSize={"sm"}>
          Coordinates used for Boundary eligibility:{" "}
          <Text
            as="a"
            href={googleMapsPlaceUrl(geom.coordinates[1], geom.coordinates[0])}
            target="_blank"
            rel="noopener noreferrer"
            color="blue.500"
            textDecoration="underline"
            cursor="pointer"
            _hover={{ color: "blue.600" }}
          >
            {`${geom.coordinates[1].toFixed(4)}, ${geom.coordinates[0].toFixed(
              4
            )}`}
          </Text>
        </Text>
      )}
      {!geom?.coordinates && !showLatLngForm && !hideCoordinateWarning && (
        <Text color={"red.500"} fontSize={"sm"}>
          {!hasFailedGeocode ? (
            <>
              {"Coordinates not found. Review address and "}
              <Text
                fontSize={"sm"}
                as="span"
                textDecoration="underline"
                cursor="pointer"
                opacity={isRetryingGeocode ? 0.5 : 1}
                pointerEvents={isRetryingGeocode ? "none" : "auto"}
                _hover={{ color: "red.600" }}
                onClick={onRetryGeocode}
              >
                {isRetryingGeocode ? "retrying..." : "retry geocoding"}
              </Text>
              {" or "}
              <Text
                fontSize={"sm"}
                as="span"
                textDecoration="underline"
                cursor="pointer"
                _hover={{ color: "red.600" }}
                onClick={onClickAcceptEnterCoordinates}
              >
                enter coordinates manually
              </Text>
              {" to support Boundary eligibility for this address."}
            </>
          ) : (
            <>
              Cannot geocode address, please{" "}
              <Text
                fontSize={"sm"}
                as="span"
                textDecoration="underline"
                cursor="pointer"
                _hover={{ color: "red.600" }}
                onClick={onClickAcceptEnterCoordinates}
              >
                enter coordinates manually
              </Text>
              {" to support Boundary eligibility for this address."}
            </>
          )}
        </Text>
      )}
      {showLatLngForm && !hideCoordinateWarning && (
        <Text color={"gray.600"} fontSize={"sm"}>
          Coordinates not found, manually enter them. Find in{" "}
          <Text
            as="a"
            href="https://maps.google.com"
            target="_blank"
            rel="noopener noreferrer"
            color="blue.500"
            textDecoration="underline"
            cursor="pointer"
            _hover={{ color: "blue.600" }}
          >
            Google Maps
          </Text>
          .
        </Text>
      )}
      {showLatLngForm && (
        <Flex gap={3} marginTop="1rem">
          <InputWithValidationControl
            isRequired={false}
            label="Latitude"
            isInvalid={!isValid}
            inputProps={{
              placeholder: "E.g.: 32.69848346626098",
              value: latValue,
              onChange: (e) => {
                const value = e.target.value;
                setLatValue(value);
                const lat = parseFloat(value);
                const lng = parseFloat(lngValue);
                if (!isNaN(lat) && !isNaN(lng)) {
                  formik.setFieldValue(withPrefix("geom"), {
                    coordinates: [lng, lat],
                  });
                } else {
                  formik.setFieldValue(withPrefix("geom"), null);
                }
              },
            }}
            name={withPrefix("latitude")}
            id="latitude"
          />
          <InputWithValidationControl
            isRequired={false}
            label="Longitude"
            isInvalid={!geom?.coordinates}
            inputProps={{
              placeholder: "E.g.: -117.11873953103859",
              value: lngValue,
              onChange: (e) => {
                const value = e.target.value;
                setLngValue(value);
                const lng = parseFloat(value);
                const lat = parseFloat(latValue);
                if (!isNaN(lng) && !isNaN(lat)) {
                  formik.setFieldValue(withPrefix("geom"), {
                    coordinates: [lng, lat],
                  });
                } else {
                  formik.setFieldValue(withPrefix("geom"), null);
                }
              },
            }}
            name={withPrefix("longitude")}
            id="longitude"
          />
        </Flex>
      )}
    </Flex>
  );
};

export const FormAddressForm: React.FC<Props> = (props) => {
  const { isRequired, isDisabled, fieldPrefix, autoFocus } = props;
  const formik = useFormikContext<PersonAddressForm>();
  const [isValid, setIsValid] = useState<boolean>(true);
  const [showLatLngForm, setShowLatLngForm] = useState<boolean>(false);
  const [isRetryingGeocode, setIsRetryingGeocode] = useState<boolean>(false);
  const [hasFailedGeocode, setHasFailedGeocode] = useState<boolean>(false);
  const hasAttemptedInitialGeocode = useRef<boolean>(false);
  const addressSectionRef = useRef<HTMLDivElement>(null);

  const withPrefix = useCallback(
    (name: string) => {
      if (!fieldPrefix) {
        return name;
      }

      return [fieldPrefix, name].join(".");
    },
    [fieldPrefix]
  );

  const [geom] = useField<Geom>(withPrefix("geom"));
  const [streetField] = useField<string>(withPrefix("street_address"));
  const [cityField] = useField<string>(withPrefix("city"));
  const [stateField] = useField<string>(withPrefix("state"));
  const [zipField] = useField<string>(withPrefix("zip_code"));

  const addressIsEmpty =
    !streetField.value &&
    !cityField.value &&
    !stateField.value &&
    !zipField.value;

  const allRequiredFieldsHaveContent =
    streetField.value &&
    streetField.value.length > 0 &&
    cityField.value &&
    cityField.value.length > 0 &&
    stateField.value &&
    stateField.value.length > 0 &&
    zipField.value &&
    zipField.value.length > 0;

  const someFieldsHaveContent =
    (streetField.value && streetField.value.length > 0) ||
    (cityField.value && cityField.value.length > 0) ||
    (stateField.value && stateField.value.length > 0) ||
    (zipField.value && zipField.value.length > 0);

  const previousAddressValues = useRef({
    street_address: streetField.value,
    city: cityField.value,
    state: stateField.value,
    zip_code: zipField.value,
  });

  // detect changes to address and clear address geometry
  // when a change occurs
  useEffect(() => {
    const prev = previousAddressValues.current;
    if (
      prev.street_address !== streetField.value ||
      prev.city !== cityField.value ||
      prev.state !== stateField.value ||
      prev.zip_code !== zipField.value
    ) {
      previousAddressValues.current = {
        street_address: streetField.value,
        city: cityField.value,
        state: stateField.value,
        zip_code: zipField.value,
      };
      formik.setFieldValue(withPrefix("geom"), null);
      setHasFailedGeocode(false);
      setShowLatLngForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    streetField.value,
    cityField.value,
    stateField.value,
    zipField.value,
    withPrefix,
  ]);

  const setAddressFields = (address: PersonAddress) => {
    for (const [key, value] of Object.entries(address)) {
      formik.setFieldValue(withPrefix(key), value ?? "");
    }

    // prevent the programatic update to geometry from triggering
    //  the autoclearing useEffect
    previousAddressValues.current = {
      street_address: address.street_address,
      city: address.city,
      state: address.state,
      zip_code: address.zip_code,
    };

    setTimeout(() => {
      formik.validateForm();
    });
  };

  const retryGeocode = useCallback(async () => {
    setIsRetryingGeocode(true);
    try {
      const coordinates = await addressLookup(new google.maps.Geocoder(), {
        street_address: streetField.value ?? "",
        street_address_line_2: "",
        city: cityField.value ?? "",
        state: stateField.value ?? "",
        zip_code: zipField.value ?? "",
      });
      if (coordinates) {
        formik.setFieldValue(withPrefix("geom"), {
          type: "Point",
          coordinates,
        });
        setHasFailedGeocode(false);
      } else {
        setHasFailedGeocode(true);
      }
    } catch (error) {
      setHasFailedGeocode(true);
    } finally {
      setIsRetryingGeocode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    withPrefix,
    streetField.value,
    cityField.value,
    stateField.value,
    zipField.value,
  ]);

  const handleAddressBlur = useCallback(() => {
    setTimeout(() => {
      const stillFocusedInSection = addressSectionRef.current?.contains(
        document.activeElement
      );
      if (
        !stillFocusedInSection &&
        allRequiredFieldsHaveContent &&
        !geom?.value?.coordinates &&
        !isRetryingGeocode &&
        !showLatLngForm
      ) {
        retryGeocode();
      }
    }, 0);
  }, [
    allRequiredFieldsHaveContent,
    geom?.value?.coordinates,
    isRetryingGeocode,
    retryGeocode,
    showLatLngForm,
  ]);

  // On first load, if we have address but no coordinates, attempt geocoding once before showing the error
  useEffect(() => {
    const geomValue = geom?.value;
    const hasNoCoordinates = !geomValue?.coordinates;
    if (
      hasNoCoordinates &&
      allRequiredFieldsHaveContent &&
      !hasAttemptedInitialGeocode.current
    ) {
      hasAttemptedInitialGeocode.current = true;
      retryGeocode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRequiredFieldsHaveContent, geom?.value?.coordinates, retryGeocode]);

  useEffect(() => {
    setIsValid(isRequired ? !!geom && !!geom.value : true);
  }, [geom, isRequired]);

  return (
    <>
      <Flex ref={addressSectionRef} direction="column" width="100%">
        <Grid
          gridTemplateColumns="repeat(auto-fit, minmax(15rem, 1fr))"
          alignItems="flex-end"
          gap={{ md: 3 }}
        >
          <AddressAutocomplete
            onAddressSelected={(address) => {
              setShowLatLngForm(false);
              setAddressFields(address);
            }}
          >
            <InputWithValidationControl
              isRequired={isRequired}
              label="Address"
              inputProps={
                {
                  placeholder: "E.g.: 532 Oakland Ave",
                  autoFocus,
                  autoComplete: "off",
                  ...PASSWORD_MANAGER_IGNORE,
                } as React.ComponentProps<
                  typeof InputWithValidationControl
                >["inputProps"]
              }
              name={withPrefix("street_address")}
              id="street_address"
              isDisabled={isDisabled}
              isInvalid={
                isRequired
                  ? !streetField.value
                  : someFieldsHaveContent && !streetField.value
                  ? true
                  : false
              }
            />
          </AddressAutocomplete>
          <InputWithValidationControl
            isRequired={false}
            inputProps={
              {
                placeholder: "E.g.: 5B",
                autoComplete: "off",
                ...PASSWORD_MANAGER_IGNORE,
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  formik
                    .getFieldProps(withPrefix("street_address_line_2"))
                    .onBlur(e);
                  handleAddressBlur();
                },
              } as React.ComponentProps<
                typeof InputWithValidationControl
              >["inputProps"]
            }
            name={withPrefix("street_address_line_2")}
            id="street_address_line_2"
            label="Apartment/suite (Optional)"
            isDisabled={isDisabled}
          />
          <InputWithValidationControl
            isRequired={isRequired}
            inputProps={
              {
                placeholder: "E.g.: New Orleans",
                autoComplete: "off",
                ...PASSWORD_MANAGER_IGNORE,
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  cityField.onBlur(e);
                  handleAddressBlur();
                },
              } as React.ComponentProps<
                typeof InputWithValidationControl
              >["inputProps"]
            }
            name={withPrefix("city")}
            id="city"
            label="City"
            isDisabled={isDisabled}
            isInvalid={
              isRequired
                ? !cityField.value
                : someFieldsHaveContent && !cityField.value
                ? true
                : false
            }
          />
          <Flex gap={3} alignItems="flex-end">
            <InputWithValidationControl
              isRequired={isRequired}
              inputProps={
                {
                  placeholder: "E.g.: LA",
                  autoComplete: "off",
                  ...PASSWORD_MANAGER_IGNORE,
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    stateField.onBlur(e);
                    handleAddressBlur();
                  },
                } as React.ComponentProps<
                  typeof InputWithValidationControl
                >["inputProps"]
              }
              name={withPrefix("state")}
              id="state"
              label="State"
              isDisabled={isDisabled}
              isInvalid={
                isRequired
                  ? !stateField.value
                  : someFieldsHaveContent && !stateField.value
                  ? true
                  : false
              }
            />
            <InputWithValidationControl
              isRequired={isRequired}
              inputProps={
                {
                  placeholder: "E.g.: 10181",
                  autoComplete: "off",
                  ...PASSWORD_MANAGER_IGNORE,
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    zipField.onBlur(e);
                    handleAddressBlur();
                  },
                } as React.ComponentProps<
                  typeof InputWithValidationControl
                >["inputProps"]
              }
              name={withPrefix("zip_code")}
              id="zip_code"
              label="Zip code"
              isDisabled={isDisabled}
              isInvalid={
                isRequired
                  ? !zipField.value
                  : someFieldsHaveContent && !zipField.value
                  ? true
                  : false
              }
            />
          </Flex>
        </Grid>
      </Flex>
      <Flex gap={3} alignItems="flex-end">
        <Coordinates
          withPrefix={withPrefix}
          formik={formik}
          isValid={isValid}
          geom={geom && geom.value ? geom.value : null}
          showLatLngForm={showLatLngForm}
          hideCoordinateWarning={
            addressIsEmpty || !allRequiredFieldsHaveContent
          }
          onClickAcceptEnterCoordinates={() => setShowLatLngForm(true)}
          onRetryGeocode={retryGeocode}
          isRetryingGeocode={isRetryingGeocode}
          hasFailedGeocode={hasFailedGeocode}
        />
      </Flex>
    </>
  );
};
